import { motion } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import "../style/EmpowerSection.css";
import { Calendar, FileText, Clock, Users, CreditCard, Clipboard } from "lucide-react";

const EmpowerSection = () => {
    const scrollContainerRef = useRef(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);

    const features = [
        {
            icon: <Users style={{ color: "#3065e0ff", width: 'fit' }} />,
            title: "Customer Management",
            description: "Store and manage customer details with quick search, active/archived views, and VAT-exempt controls.",
            color: "#3065e0"
        },
        {
            icon: <Clock style={{ color: "#FF8C00", width: 'fit' }} />,
            title: "Job Scheduling",
            description: "Create one-off or recurring jobs, assign team members, and track every detail in one streamlined workflow.",
            color: "#FF8C00"
        },
        {
            icon: <FileText style={{ color: "rgba(23, 37, 167, 1)", width: 'fit' }} />,
            title: "Quotes & Approvals",
            description: "Send quotes by Email or SMS and let customers approve or decline instantly — with automatic status updates.",
            color: "#3065e0"
        },
        {
            icon: <CreditCard style={{ color: "#10B981", width: 'fit' }} />,
            title: "Invoices & Payments",
            description: "Accept card, cash, and bank transfer payments with automatic reminders to help you get paid faster and keep cashflow smooth.",
            color: "#10B981"
        },
        {
            icon: <Calendar style={{ color: "#EC4899", width: 'fit' }} />,
            title: "Smart Calendar",
            description: "Daily, weekly, and monthly views with click-to-open jobs. Drag and drop on desktop, or tap to quickly change dates on mobile and tablet — even for recurring work.",
            color: "#EC4899"
        },
        {
            icon: <Clipboard style={{ color: "#06B6D4", width: 'fit' }} />,
            title: "Task Templates",
            description: "Save time by creating reusable priced tasks. Add them to jobs instantly and adjust quantities as needed.",
            color: "#06B6D4"
        }
    ];

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            const container = scrollContainerRef.current;
            if (!container) return;

            const scrollLeft = container.scrollLeft;
            const cardWidth = container.querySelector('.feature-card-control')?.offsetWidth || 0;
            const gap = isMobile ? 20 : 30;

            // Calculate which card is most visible
            const index = Math.round(scrollLeft / (cardWidth + gap));
            setActiveIndex(Math.min(Math.max(0, index), features.length - 1));
        };

        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', handleScroll);
            handleScroll(); // Initial call
        }

        return () => {
            if (container) {
                container.removeEventListener('scroll', handleScroll);
            }
        };
    }, [features.length, isMobile]);

    const scrollToCard = (index) => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const cardWidth = container.querySelector('.feature-card-control')?.offsetWidth || 0;
        const gap = isMobile ? 20 : 30;
        const scrollPosition = index * (cardWidth + gap);

        container.scrollTo({
            left: scrollPosition,
            behavior: 'smooth'
        });
    };

    const handlePrevious = () => {
        if (activeIndex > 0) {
            scrollToCard(activeIndex - 1);
        }
    };

    const handleNext = () => {
        if (activeIndex < features.length - 1) {
            scrollToCard(activeIndex + 1);
        }
    };

    const cardVariants = {
        hidden: { opacity: 0 },
        visible: (i) => ({
            opacity: 1,
            x: 0,
            transition: {
                delay: i * 0.1,
                duration: 0.5
            }
        })
    };

    return (
        <section className="control-section"
            style={{
                backgroundColor: "#f9fafb",
            }}>
            <div className="container">
                {/* Section Header */}
                <motion.div
                    className="control-header"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <h2 className="section-title-control">Run Your Business</h2>
                    <p className="section-subtitle-control">
                        Your essential tools for creating jobs, managing customers, and keeping everything organised.
                    </p>
                </motion.div>

                {/* Horizontal Scrollable Cards */}
                <div className="scroll-wrapper-control">
                    {/* Previous Button - Desktop Only */}
                    {!isMobile && (
                        <button
                            className={`nav-button-control nav-button-left-control ${activeIndex === 0 ? 'disabled' : ''}`}
                            onClick={handlePrevious}
                            disabled={activeIndex === 0}
                            aria-label="Previous card"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    )}

                    <motion.div
                        className="features-scroll-control"
                        ref={scrollContainerRef}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-100px" }}
                    >
                        {features.map((feature, index) => (
                            <motion.div
                                key={index}
                                className="feature-card-control"
                                custom={index}
                                variants={cardVariants}
                                whileHover={{
                                    y: -10,
                                    transition: { duration: 0.3 }
                                }}
                            >
                                <div
                                    className="feature-icon-wrapper-control"
                                    style={{ backgroundColor: `${feature.color}20` }}
                                >
                                    <span className="feature-icon-control">
                                        {feature.icon}
                                    </span>
                                </div>
                                <h3 className="feature-title-control">{feature.title}</h3>
                                <p className="feature-description-control">{feature.description}</p>
                            </motion.div>
                        ))}
                    </motion.div>

                    {/* Next Button - Desktop Only */}
                    {!isMobile && (
                        <button
                            className={`nav-button-control nav-button-right-control ${activeIndex === features.length - 1 ? 'disabled' : ''}`}
                            onClick={handleNext}
                            disabled={activeIndex === features.length - 1}
                            aria-label="Next card"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    )}

                    {/* Scroll Indicators (Bubbles) */}
                    <div className="scroll-indicators">
                        {features.map((_, index) => (
                            <button
                                key={index}
                                className={`scroll-bubble ${activeIndex === index ? 'active' : ''}`}
                                onClick={() => scrollToCard(index)}
                                aria-label={`Scroll to card ${index + 1}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default EmpowerSection;